import { api } from "@/lib/auth";
import { API_BASE } from "@/lib/apiBase";

let current = null;

export const stopVoice = () => {
  if (current) {
    current.pause();
    current = null;
  }
};

// Ariel's voice is kept on the device (Cache Storage), so a practice heard once plays again offline.
const VOICE_CACHE = "ariel-voice-v1";
const voiceKey = (text) => {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return `/ariel-voice/${text.length}-${(h >>> 0).toString(36)}`;
};
const voiceBlob = async (text) => {
  const cache = typeof caches !== "undefined" ? await caches.open(VOICE_CACHE).catch(() => null) : null;
  const hit = await cache?.match(voiceKey(text));
  if (hit) return hit.blob();
  const { data } = await api.post("/ariel/voice", { text }, { responseType: "blob" });
  cache?.put(voiceKey(text), new Response(data, { headers: { "Content-Type": data.type || "audio/mpeg" } })).catch(() => {});
  return data;
};

// Plays Goddess Ariel's spoken voice for a piece of text. Resolves when playback ends.
export async function playVoice(text, { onStart } = {}) {
  stopVoice();
  const data = await voiceBlob(text);
  const url = URL.createObjectURL(data);
  const audio = new Audio(url);
  current = audio;
  return new Promise((resolve) => {
    const done = () => {
      URL.revokeObjectURL(url);
      if (current === audio) current = null;
      resolve();
    };
    audio.onended = done;
    audio.onerror = done;
    audio.onpause = () => { if (!audio.ended && audio.currentTime < (audio.duration || Infinity) - 0.05) stopped = true; done(); };
    audio.play().then(() => onStart?.()).catch(done);
  });
}
let stopped = false;

// Plays a list of texts one after another; onIndex(k) fires before each. Stops when any voice is stopped.
export async function playAll(texts, onIndex) {
  stopped = false;
  for (let k = 0; k < texts.length; k += 1) {
    onIndex?.(k);
    await new Promise((r) => setTimeout(r, 250));
    if (stopped) return;
    await playVoice(texts[k]).catch(() => {});
    if (stopped) return;
  }
}

// Streams Ariel's reply token by token. Calls onToken for each piece; throws with a friendly message on failure.
export async function streamAriel(text, onToken, space = "gates") {
  const send = () => fetch(`${API_BASE}/ariel/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, space }) });
  let res = await send();
  if (res.status === 401) {
    await api.post("/auth/refresh");
    res = await send();
  }
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(typeof d?.detail === "string" ? d.detail : "Ariel could not answer just now. Please try again.");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (!line.startsWith("data:")) continue;
      const ev = JSON.parse(line.slice(5));
      if (ev.error) throw new Error(ev.error);
      if (ev.t) onToken(ev.t);
    }
  }
}
